import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { loadPublications } from '../content/publications';

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const renderMarkdown = (text) => {
  if (!text) {
    return null;
  }
  const lines = text.split('\n');
  const blocks = [];
  let listBuffer = [];

  const renderInline = (value, keyPrefix) => {
    const pattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;
    const parts = [];
    let keyIndex = 0;

    while ((match = pattern.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push(value.slice(lastIndex, match.index));
      }
      parts.push(
        <a
          key={`${keyPrefix}-link-${keyIndex}`}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[1]}
        </a>,
      );
      keyIndex += 1;
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < value.length) {
      parts.push(value.slice(lastIndex));
    }

    return parts;
  };

  const flushList = (keyBase) => {
    if (listBuffer.length === 0) {
      return;
    }
    const items = [...listBuffer];
    listBuffer = [];
    blocks.push(
      <ul key={`${keyBase}-list`} className="publication-detail__list">
        {items.map((item, index) => (
          <li key={`${keyBase}-item-${index}`}>{renderInline(item, `${keyBase}-li-${index}`)}</li>
        ))}
      </ul>,
    );
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList(index);
      return;
    }
    if (trimmed.startsWith('- ')) {
      listBuffer.push(trimmed.slice(2));
      return;
    }
    flushList(index);
    if (trimmed.startsWith('## ')) {
      blocks.push(
        <h2 key={`h2-${index}`} className="publication-detail__heading">
          {trimmed.slice(3)}
        </h2>,
      );
      return;
    }
    if (trimmed.startsWith('# ')) {
      blocks.push(
        <h1 key={`h1-${index}`} className="publication-detail__title">
          {trimmed.slice(2)}
        </h1>,
      );
      return;
    }
    blocks.push(
      <p key={`p-${index}`} className="publication-detail__paragraph">
        {renderInline(trimmed, `p-${index}`)}
      </p>,
    );
  });

  flushList('end');

  return blocks;
};

export default function PublicationDetailPage() {
  const { slug } = useParams();
  const [publication, setPublication] = useState(null);
  const [loadState, setLoadState] = useState('loading');

  useEffect(() => {
    let isActive = true;

    loadPublications()
      .then((items) => {
        if (!isActive) {
          return;
        }
        const match = items.find((item) => item.slug === slug);
        if (!match || match.draft) {
          setLoadState('not-found');
          return;
        }
        setPublication(match);
        setLoadState('ready');
      })
      .catch(() => {
        if (isActive) {
          setLoadState('error');
        }
      });

    return () => {
      isActive = false;
    };
  }, [slug]);

  const seoTitle = publication?.seo?.title || publication?.title || 'Publication';
  const seoDescription = publication?.seo?.description || publication?.summary || '';
  const seoImage = publication?.seo?.image || publication?.heroImage || '/og-card.jpg';

  const articleSchema = publication
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: publication.title,
        description: seoDescription,
        image: seoImage.startsWith('http') ? seoImage : `https://phennx.io${seoImage}`,
        datePublished: publication.date,
        mainEntityOfPage: `https://phennx.io/publications/${publication.slug}`,
        author: {
          '@type': 'Organization',
          name: publication.author || 'Phennx',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Phennx',
          logo: {
            '@type': 'ImageObject',
            url: 'https://phennx.io/logo-full-transparent.png',
          },
        },
      }
    : undefined;

  const bodyContent = useMemo(() => renderMarkdown(publication?.body), [publication]);

  if (loadState === 'loading') {
    return (
      <div className="publication-detail">
        <section className="section">
          <div className="section-inner publication-detail__loading">
            <p className="publications-coming__title">Loading publication.</p>
            <p className="publications-coming__copy">Fetching the latest details.</p>
          </div>
        </section>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="publication-detail">
        <section className="section">
          <div className="section-inner publication-detail__loading">
            <p className="publications-coming__title">Publication unavailable.</p>
            <p className="publications-coming__copy">
              Please try again in a moment or return to the news page.
            </p>
            <Link className="btn-secondary publication-detail__cta" to="/blog">
              Back to news
            </Link>
          </div>
        </section>
      </div>
    );
  }

  if (loadState === 'not-found') {
    return (
      <div className="publication-detail">
        <section className="section">
          <div className="section-inner publication-detail__loading">
            <p className="publications-coming__title">Publication not found.</p>
            <p className="publications-coming__copy">We could not find that entry.</p>
            <Link className="btn-secondary publication-detail__cta" to="/blog">
              Back to news
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/publications/${publication.slug}`}
        image={seoImage}
        type="article"
        schema={articleSchema}
      />
      <article className="publication-detail" aria-label={publication.title}>
        <header className="publication-detail__hero">
          <div className="section-inner">
            <div className="publication-detail__eyebrow">{publication.category || 'Publication'}</div>
            {publication.heroImage && (
              <div className="publication-detail__hero-media">
                <img src={publication.heroImage} alt={publication.title} loading="lazy" />
              </div>
            )}
            <h1 className="publication-detail__headline">{publication.title}</h1>
            {publication.summary && (
              <p className="publication-detail__summary">{publication.summary}</p>
            )}
            <div className="publication-detail__meta">
              {publication.author && <span>{publication.author}</span>}
              {publication.date && <span>{formatDate(publication.date)}</span>}
              {publication.readTime && <span>{publication.readTime}</span>}
            </div>
          </div>
        </header>
        <section className="section">
          <div className="section-inner publication-detail__body">{bodyContent}</div>
        </section>
        <section className="section publication-detail__footer">
          <div className="section-inner">
            <Link className="btn-secondary publication-detail__cta" to="/blog">
              Back to news
            </Link>
          </div>
        </section>
      </article>
    </>
  );
}
